import { mergeProps } from '@react-native-aria/utils'
import {
  Children,
  cloneElement,
  isValidElement,
  type PropsWithChildren,
  useMemo,
  type ReactElement,
  type Ref,
} from 'react'

import { useAriaButton, type TAriaButtonProps } from '@/src/elements/button/useAriaButton'

import type { TActionSheetProps } from '../action/ActionSheetMobile'
import { usePopoverContext } from './PopoverContext'
import type { TUsePopoverReturn } from './usePopover'

type TTriggerChildProps = Partial<TAriaButtonProps>

/**
 * RegularPopoverTrigger opens the popover's content. It must be an interactive element such as a button.
 */
const RegularPopoverTrigger = ({
  children,
  getTriggerProps,
  triggerRef,
}: PropsWithChildren<Pick<TUsePopoverReturn, 'triggerRef' | 'getTriggerProps'>>) => {
  // Force a single child
  const child = useMemo<ReactElement & { ref?: Ref<ReactElement> }>(() => {
    if (typeof children === 'string') return <p>{children}</p>

    if (isValidElement(children)) {
      return Children.only(children)
    }

    throw new Error('PopoverTrigger expects a valid ReactElement or a string as child')
  }, [children])

  const { onPress, isDisabled, ...rest } = useMemo(
    // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- child.props is typed as `unknown`
    () => getTriggerProps(child.props as TTriggerChildProps, child.ref),
    [getTriggerProps, child.props, child.ref],
  )

  const { buttonProps } = useAriaButton({ onPress, isDisabled }, triggerRef)

  return cloneElement(child, mergeProps(rest, buttonProps))
}

export const PopoverTrigger = ({ children }: Pick<TActionSheetProps, 'children'>) => {
  const { triggerRef, getTriggerProps } = usePopoverContext()

  if (typeof children === 'function') return children({ onPress: getTriggerProps().onPress })

  return (
    <RegularPopoverTrigger triggerRef={triggerRef} getTriggerProps={getTriggerProps}>
      {children}
    </RegularPopoverTrigger>
  )
}
